import React, { useState, useMemo, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { getSectionComponent } from '../../../sections';
import ResponsiveFrame from '../../../components/ResponsiveFrame';
import { getThemeStyles, getThemeFontsUrl } from '../../../lib/themes';
import SectionToolbar from './SectionToolbar';
import InsertionPoint from './InsertionPoint';

const PageCanvas = ({
  sections,
  onSelectSection,
  selectedSectionId,
  onMoveSection,
  onDeleteSection,
  onDuplicateSection,
  onInsertSection,
  currentViewport = 'desktop',
  theme = 'default',
}) => {
  // В редакторе используем inline styles, чтобы не влиять на админку
  const themeStyles = getThemeStyles(theme);

  // Загружаем шрифты темы в основной документ (ResponsiveFrame скопирует в iframe)
  useEffect(() => {
    const fontsUrl = getThemeFontsUrl(theme);
    if (!fontsUrl) return;

    const fontLinkId = `theme-fonts-${theme}`;
    if (document.getElementById(fontLinkId)) return; // уже загружен

    const fontLink = document.createElement('link');
    fontLink.id = fontLinkId;
    fontLink.rel = 'stylesheet';
    fontLink.href = fontsUrl;
    document.head.appendChild(fontLink);
  }, [theme]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);

  // Viewport pixel widths for iframe
  const viewportPixelWidths = {
    desktop: '100%',
    tablet: '768px',
    mobile: '375px'
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e?.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDropTargetIndex(index);
    }
  };

  const handleDrop = (e, index) => {
    e?.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      onMoveSection(draggedIndex, index);
    }
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const renderSectionPreview = (section) => {
    // Проверяем, есть ли компонент в реестре секций
    const RegistryComponent = getSectionComponent(section?.type);
    if (RegistryComponent) {
      return (
        <RegistryComponent
          data={section?.content || {}}
          styles={section?.styles || {}}
          theme={theme}
          isEditing={true}
        />
      );
    }

    // Легаси рендер для старых компонентов
    switch (section?.type) {
      case 'hero-1':
        return (
          <div className="relative h-64 md:h-80 lg:h-96 bg-gradient-to-br from-primary to-secondary rounded-lg overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center text-white">
              <div className="text-center px-4">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
                  {section?.content?.title || 'Заголовок баннера'}
                </h1>
                <p className="text-base md:text-lg lg:text-xl opacity-90 mb-6">
                  {section?.content?.subtitle || 'Подзаголовок с описанием'}
                </p>
                <div className="inline-block px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium">
                  Забронировать
                </div>
              </div>
            </div>
          </div>
        );

      case 'gallery-1':
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3 lg:gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8]?.map(i => (
              <div key={i} className="aspect-square bg-muted rounded-lg overflow-hidden">
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="Image" size={32} className="opacity-30" />
                </div>
              </div>
            ))}
          </div>
        );

      case 'pricing-1':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3]?.map(i => (
              <div key={i} className="p-6 bg-card border border-border rounded-xl">
                <h3 className="text-lg md:text-xl font-semibold mb-2">Тариф {i}</h3>
                <div className="text-2xl md:text-3xl font-bold text-primary mb-4">
                  {i * 1000} ₽<span className="text-sm font-normal text-text-secondary">/час</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <Icon name="Check" size={16} color="var(--color-success)" />
                    <span>Функция {i}.1</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Icon name="Check" size={16} color="var(--color-success)" />
                    <span>Функция {i}.2</span>
                  </li>
                </ul>
                <div className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-center font-medium">
                  Выбрать
                </div>
              </div>
            ))}
          </div>
        );

      case 'testimonials-1':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3]?.map(i => (
              <div key={i} className="p-6 bg-card border border-border rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    <Icon name="User" size={24} className="opacity-50" />
                  </div>
                  <div>
                    <p className="font-semibold">Клиент {i}</p>
                    <p className="text-sm text-text-secondary">Компания</p>
                  </div>
                </div>
                <p className="text-sm text-text-secondary">
                  Отличное пространство для работы. Все необходимое есть, персонал вежливый.
                </p>
              </div>
            ))}
          </div>
        );

      case 'contact-1':
        return (
          <div className="max-w-2xl mx-auto p-6 bg-card border border-border rounded-xl">
            <h3 className="text-xl md:text-2xl font-semibold mb-6">Свяжитесь с нами</h3>
            <div className="space-y-4">
              <div className="h-10 bg-muted rounded-lg"></div>
              <div className="h-10 bg-muted rounded-lg"></div>
              <div className="h-24 bg-muted rounded-lg"></div>
              <div className="h-10 bg-primary rounded-lg"></div>
            </div>
          </div>
        );

      case 'widget-booking-start': case'widget-cabinet-details': case'widget-cabinet-list':
        return (
          <div className="p-8 bg-gradient-to-br from-accent/10 to-accent/5 border-2 border-dashed border-accent rounded-xl">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
                <Icon name="Calendar" size={32} color="var(--color-accent)" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Виджет "Аренда Вместе"</h3>
              <p className="text-sm text-text-secondary mb-4">
                {section?.type === 'widget-booking-start' && 'Виджет начала бронирования'}
                {section?.type === 'widget-cabinet-details' && 'Виджет деталей кабинета'}
                {section?.type === 'widget-cabinet-list' && 'Виджет списка кабинетов'}
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-card rounded-lg text-sm">
                <Icon name="Settings" size={16} />
                <span>Настроить параметры</span>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="p-8 bg-muted rounded-lg text-center">
            <Icon name="Layout" size={48} className="mx-auto mb-4 opacity-30" />
            <p className="text-text-secondary">Секция: {section?.name}</p>
          </div>
        );
    }
  };

  // Контент секций (будет рендериться внутри iframe для tablet/mobile)
  const sectionsContent = (
    <div className="p-4 pb-24">
      {sections?.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
              <Icon name="Plus" size={40} className="opacity-30" />
            </div>
            <h3 className="text-xl md:text-2xl font-semibold mb-2">Начните создавать страницу</h3>
            <p className="text-text-secondary max-w-md mb-6">
              Нажмите "+" чтобы добавить первую секцию на страницу
            </p>
            {onInsertSection && (
              <button
                onClick={() => onInsertSection(0)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                <Icon name="Plus" size={18} />
                Добавить секцию
              </button>
            )}
          </div>
        ) : (
          <div>
            {/* Insertion point at the very top */}
            {onInsertSection && (
              <InsertionPoint onClick={() => onInsertSection(0)} />
            )}

            {sections?.map((section, index) => (
              <React.Fragment key={section?.id}>
                <div
                  id={`section-${index}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`relative group transition-all duration-200 ${
                    draggedIndex === index ? 'opacity-50 scale-[0.98]' : ''
                  } ${dropTargetIndex === index ? 'border-t-4 border-primary' : ''}`}
                >
                  {/* Hover/selected outline */}
                  <div className={`absolute inset-0 pointer-events-none rounded-lg transition-all duration-200 ${
                    selectedSectionId === section?.id
                      ? 'border-2 border-primary ring-4 ring-primary/10'
                      : 'border-2 border-transparent group-hover:border-dashed group-hover:border-primary/30'
                  }`} />

                  {/* Floating toolbar */}
                  <SectionToolbar
                    sectionIndex={index}
                    totalSections={sections.length}
                    isSelected={selectedSectionId === section?.id}
                    onMoveUp={() => onMoveSection(index, index - 1)}
                    onMoveDown={() => onMoveSection(index, index + 1)}
                    onDuplicate={() => onDuplicateSection?.(section?.id)}
                    onOpenSettings={() => onSelectSection(section?.id)}
                    onDelete={() => onDeleteSection(section?.id)}
                  />

                  <div
                    onClick={() => onSelectSection(section?.id)}
                    className="cursor-pointer"
                  >
                    {renderSectionPreview(section)}
                  </div>
                </div>

                {/* Insertion point between sections */}
                {onInsertSection && (
                  <InsertionPoint onClick={() => onInsertSection(index + 1)} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
    </div>
  );

  // Мемоизируем контент для iframe (вызываем useMemo всегда, до условных return)
  const previewContent = useMemo(() => (
    <div className="min-h-full bg-background" style={themeStyles}>
      <div className="p-4">
        {sections?.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
              <Icon name="Smartphone" size={40} className="opacity-30" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Нет секций</h3>
            <p className="text-text-secondary">
              Добавьте секции в режиме Desktop
            </p>
          </div>
        ) : (
          <div className="space-y-0">
            {sections.map((section, index) => (
              <div key={section?.id} id={`section-${index}`}>
                {renderSectionPreview(section)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  ), [sections, themeStyles]);

  // Для desktop рендерим напрямую
  if (currentViewport === 'desktop') {
    return (
      <div className="bg-background lg:h-full lg:overflow-y-auto" style={themeStyles}>
        <div className="max-w-7xl mx-auto">
          {sectionsContent}
        </div>
      </div>
    );
  }

  // Для tablet/mobile используем iframe
  return (
    <div className="overflow-hidden bg-muted flex flex-col lg:h-full">
      {/* Инфо-бар */}
      <div className="flex-shrink-0 px-4 py-2 bg-warning/10 border-b border-warning/20 text-center">
        <p className="text-sm text-warning flex items-center justify-center gap-2">
          <Icon name="Info" size={16} />
          Режим просмотра. Для редактирования переключитесь на Desktop.
        </p>
      </div>

      {/* Превью в iframe */}
      <div className="flex-1 overflow-auto flex justify-center p-4">
        <div
          className="bg-background shadow-lg rounded-lg overflow-hidden flex-shrink-0"
          style={{ width: viewportPixelWidths[currentViewport], maxWidth: '100%' }}
        >
          <ResponsiveFrame
            width="100%"
            height="600px"
          >
            {previewContent}
          </ResponsiveFrame>
        </div>
      </div>
    </div>
  );
};

export default PageCanvas;
